import { Form, InputNumber, Radio, Tooltip } from 'antd';
import type { FormInstance } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { DEFAULT_LIMIT_BEHAVIOR } from '../../store/limit/utils';
import type { CostAdjustTargetBehavior } from '../../store/limit/utils';

const BEHAVIOR_HELP_TEXT: Record<CostAdjustTargetBehavior, string> = {
  above:
    "Shipium will keep this carrier's share of shipments above the target percentage. When the carrier's share drops below the target, Shipium subsidizes its cost to make it more competitive during carrier selection. When the share is at or above the target, no adjustment is applied.",
  at: "Shipium will keep this carrier's share of shipments at the target percentage. When the carrier's share is below the target, Shipium subsidizes its cost to pull it up. When the share is above the target, Shipium penalizes its cost to pull it down. Adjustment is applied in whichever direction is needed to drive the share toward the target.",
  below:
    "Shipium will keep this carrier's share of shipments below the target percentage. When the carrier's share exceeds the target, Shipium penalizes its cost to make it less competitive during carrier selection. When the share is at or below the target, no adjustment is applied.",
};

const TARGET_PERCENT_TOOLTIP =
  'Expressed as a percentage from 0 to 100. For example, a target of 40% means Shipium will steer this carrier to cover roughly 40% of eligible shipments — pushed up, down, or to exactly that share depending on the limit behavior you chose.';

const TARGET_PERCENT_HELP =
  "The target share of shipments for this carrier. Shipium adjusts the carrier's cost relative to this number based on the selected limit behavior.";

// Sign convention: `seedAdjustment` is negated and applied to non-target
// carriers in DCAS (DynamicCostAdjustmentServiceClient.java applies
// `-seedAdjustment` via `.withNonTargetAdjustment()`), so a negative input
// makes non-target carriers more expensive — pulling the target carrier's
// share UP. Copy below reflects this verified behavior.
const FALLBACK_HELP =
  "Optional cost adjustment used when Shipium has no recent shipment data for this carrier — for example, before any shipments have been recorded (cold start) or during an extended gap in data such as a fulfillment center shutdown. Negative values increase this carrier's share of shipments; positive values decrease it. Applied as a raw number against the shipment's rated cost; currency follows the shipment's rated cost. Leave blank to use zero.";

const FALLBACK_TOOLTIP =
  'Shipium falls back to this value whenever real shipment data is unavailable. Two common cases: (1) cold start — a brand-new carrier or limit with no shipment history yet; (2) an extended data gap — for example, a week-long fulfillment center shutdown that halts shipments from an origin. Once real shipment data flows again, Shipium ignores this value and uses actuals.';

export interface DynamicLimitPercentageSettingsProps {
  form: FormInstance;
}

export const COST_ADJUST_BEHAVIOR_FIELD = 'costAdjustTargetBehavior';
export const TARGET_PERCENT_FIELD = 'targetPercent';
export const SEED_ADJUSTMENT_FIELD = 'seedAdjustment';

export const DynamicLimitPercentageSettings = ({ form }: DynamicLimitPercentageSettingsProps) => {
  const behavior: CostAdjustTargetBehavior = Form.useWatch(COST_ADJUST_BEHAVIOR_FIELD, form) ?? DEFAULT_LIMIT_BEHAVIOR;

  return (
    <>
      <Form.Item
        label="Limit behavior"
        name={COST_ADJUST_BEHAVIOR_FIELD}
        rules={[{ required: true, message: 'Limit behavior is required.' }]}
        extra={BEHAVIOR_HELP_TEXT[behavior]}
      >
        <Radio.Group optionType="button">
          <Radio.Button value="above">Keep Above Target</Radio.Button>
          <Radio.Button value="at">Keep At Target</Radio.Button>
          <Radio.Button value="below">Keep Below Target</Radio.Button>
        </Radio.Group>
      </Form.Item>

      <Form.Item
        label={
          <>
            Target Percentage{' '}
            <Tooltip title={TARGET_PERCENT_TOOLTIP}>
              <InfoCircleOutlined style={{ marginLeft: 4 }} />
            </Tooltip>
          </>
        }
        name={TARGET_PERCENT_FIELD}
        rules={[{ required: true, message: 'Target Percentage is required.' }]}
        extra={TARGET_PERCENT_HELP}
      >
        <InputNumber min={0} max={100} placeholder="No Target Percentage Set" addonAfter=" %" stringMode />
      </Form.Item>

      <Form.Item
        label={
          <>
            Fallback Cost Adjustment{' '}
            <Tooltip title={FALLBACK_TOOLTIP}>
              <InfoCircleOutlined style={{ marginLeft: 4 }} />
            </Tooltip>
          </>
        }
        name={SEED_ADJUSTMENT_FIELD}
        extra={FALLBACK_HELP}
      >
        <InputNumber precision={2} placeholder="Optional" />
      </Form.Item>
    </>
  );
};
